// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import createQuote from "@adl/App/Handlers/CustomQuote/CreateCustomQuote";
import getQuotesCollection from "@adl/App/Handlers/CustomQuote/GetCustomQuotesCollection";
import { xScreen } from "@adl/XDL/Utilities";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    createQuote,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqMyQuotesHeaderStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    textGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    createButton?: ButtonPresentationProps;
}

export const rfqMyQuotesHeaderStyles: RfqMyQuotesHeaderStyles = {
    modal: {
        sizeVariant: "small",
    },
    textGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
        css: css`
            justify-content: space-between;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
    createButton: {
        css: css`
            ${xScreen("lte", "sm")} {
                width: 100%;
            }
        `,
    },
};

const styles = rfqMyQuotesHeaderStyles;

const MyCustomQuotesHeader = ({ createQuote }: Props) => {
    const [modalisOpen, setModalisOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [newQuoteName, setNewQuoteName] = useState("");
    const [newQuoteNameError, setNewQuoteNameError] = useState("");
    const toasterContext = useContext(ToasterContext);
    const dispatch = useDispatch();

    const buttonClickHandler = () => {
        setModalisOpen(true);
    };

    const modalCloseHandler = () => {
        setModalisOpen(false);
        setNewQuoteName("");
        setIsLoading(false);
        setNewQuoteNameError("");
    };

    const inputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewQuoteNameError("");
        setNewQuoteName(event.target.value);
    };

    const createNewQuote = () => {
        if (newQuoteName === "") {
            setNewQuoteNameError(translate("Quote Name is Required"));
            return;
        }
        setIsLoading(true);
        createQuote({
            name: newQuoteName,
            onSuccess(result) {
                onSubmitSuccess();
            },
            onError(result) {
                onSubmitError(result);
            },
        });
    };

    const onSubmitSuccess = () => {
        setIsLoading(false);
        dispatch(getQuotesCollection({} as any));
        modalCloseHandler();
        toasterContext.addToast({
            body: translate("New Quote Created"),
            messageType: "success",
        });
    };

    const onSubmitError = (errorMessage: string) => {
        setIsLoading(false);
        setNewQuoteNameError(errorMessage);
    };

    return (
        <>
            <Button {...styles.createButton} onClick={buttonClickHandler}>
                {translate("New Quote")}
            </Button>
            <Modal
                size={500}
                headline={translate("New Custom Quote")}
                isOpen={modalisOpen}
                handleClose={modalCloseHandler}
            >
                <LoadingOverlay loading={isLoading}>
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.textGridItem}>
                            <TextField
                                label={translate("Quote Name")}
                                name="name"
                                value={newQuoteName}
                                error={newQuoteNameError}
                                onChange={inputChangeHandler}
                                required={true}
                            />
                        </GridItem>
                        <GridItem {...styles.buttonsGridItem}>
                            <Button {...styles.cancelButton} onClick={modalCloseHandler}>
                                {translate("Cancel")}
                            </Button>
                            <Button {...styles.submitButton} onClick={createNewQuote}>
                                {translate("Create")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </LoadingOverlay>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(MyCustomQuotesHeader),
    definition: {
        group: "Custom Quote" as any,
        displayName: "Create Custom Quote Button",
        allowedContexts: ["CustomQuotesPage" as any],
    },
};

export default widgetModule;
